import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, CircleDot, Eye, Inbox, MailCheck, Search, UserX,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getContactInquiries } from '@/lib/api';
import {
  INQUIRY_STATUSES,
  INQUIRY_SUBJECT_OPTIONS,
  displayValue,
  formatDate,
  inquiryStatusMeta,
  inquirySubjectLabel,
} from '@/lib/websiteEngagement';
import WebsiteEngagementShell from '@/components/admin/WebsiteEngagementShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DesktopTableOnly,
  MobileCard,
  MobileCardField,
  MobileCardFields,
  MobileCardHeader,
  MobileCardList,
  MobileCardNotice,
  ROW_DENSITY_CLASSES,
  StatusPill,
  TableEmptyRow,
  TableFilterCard,
  adminInputClass,
  filterPillActiveClass,
  filterPillClass,
  tableCardClass,
  tableCellClass,
  tableHeadCellClass,
  tableHeaderRowClass,
  tableRowClass,
} from '@/components/admin/AdminTableStyles';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 25;
const ALL = 'all'; // sentinel for "no filter" (Radix Select cannot use "")
const UNASSIGNED = 'unassigned';

// Same column model as the Website Signups table: a floor each column will not
// shrink past, and a share of whatever space is left over, so the header tracks
// the card on a wide screen and the table scrolls rather than crushing on a
// narrow one.
const COLUMNS = [
  { id: 'reference', label: 'Reference', min: 130, grow: 0.8 },
  { id: 'name', label: 'From', min: 150, grow: 1.2 },
  { id: 'email', label: 'Email', min: 180, grow: 1.5 },
  { id: 'subject', label: 'Subject', min: 140, grow: 1 },
  { id: 'message', label: 'Message', min: 200, grow: 1.8 },
  { id: 'owner', label: 'Owner', min: 130, grow: 0.9 },
  { id: 'status', label: 'Status', min: 110, grow: 0.7 },
  { id: 'received', label: 'Received', min: 110, grow: 0.8 },
  { id: 'actions', label: 'Actions', min: 90, grow: 0, fixed: true },
];

const GRID_TEMPLATE_COLUMNS = COLUMNS
  .map((column) => (column.fixed ? `${column.min}px` : `minmax(${column.min}px, ${column.grow}fr)`))
  .join(' ');
const TABLE_MIN_WIDTH = COLUMNS.reduce((total, column) => total + column.min, 0);
const ROW_PADDING = ROW_DENSITY_CLASSES.comfortable;
const SKELETON_ROWS = 5;
const STICKY_RIGHT = 'sticky right-0 z-10 bg-white group-hover:bg-[#F7F8FA]';
const STICKY_HEAD_RIGHT = 'sticky right-0 z-20 bg-[#062B1A]';

const EMPTY_SUMMARY = {
  total: 0, new: 0, open: 0, replied: 0, closed: 0, spam: 0, unread: 0, unassigned: 0,
};

function InquiryStatCard({ label, value, icon: Icon, tone, accent = false }) {
  return (
    <Card className={cn('border border-[rgba(6,43,26,0.08)] shadow-sm', accent && 'gold-border-left')}>
      {/* Two-up and tighter on a phone. At full width each of these is ~250px
          tall, so a row of them becomes a wall to scroll past before reaching
          the records they describe — seven of them on the signups list. */}
      <CardContent className="p-3 sm:p-4">
        <div className="mb-2 flex items-center justify-between gap-2 sm:mb-3 sm:gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/50 sm:text-xs sm:tracking-[0.1em]">
            {label}
          </span>
          <span className={cn('rounded-full p-1 sm:p-1.5', tone)}>
            <Icon className="size-3.5 sm:size-4" />
          </span>
        </div>
        <p
          className="text-2xl font-bold text-[#062B1A] sm:text-3xl"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

export default function AdminContactInquiries() {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState(ALL);
  const [subjectFilter, setSubjectFilter] = useState(ALL);
  const [ownerFilter, setOwnerFilter] = useState(ALL);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0); // 0-based

  useEffect(() => {
    const id = window.setTimeout(() => setQ(searchInput.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, subjectFilter, ownerFilter, unreadOnly, dateFrom, dateTo, q]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (statusFilter !== ALL) params.status = statusFilter;
      if (subjectFilter !== ALL) params.subject = subjectFilter;
      if (ownerFilter !== ALL) params.owner_user_id = ownerFilter;
      if (unreadOnly) params.unread = true;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (q) params.q = q;
      const { data } = await getContactInquiries(params);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
      setSummary({ ...EMPTY_SUMMARY, ...(data?.summary || {}) });
      setOwners(Array.isArray(data?.owners) ? data.owners : []);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not load contact inquiries');
      setItems([]);
      setTotal(0);
      setSummary(EMPTY_SUMMARY);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, subjectFilter, ownerFilter, unreadOnly, dateFrom, dateTo, q, page]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, load]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * PAGE_SIZE + items.length);
  const hasActiveFilters = statusFilter !== ALL || subjectFilter !== ALL
    || ownerFilter !== ALL || unreadOnly || !!dateFrom || !!dateTo || !!q;

  const headerCount = useMemo(() => {
    if (loading) return 'Loading…';
    return `${rangeStart}–${rangeEnd} of ${total} inquir${total === 1 ? 'y' : 'ies'}`;
  }, [loading, rangeStart, rangeEnd, total]);

  const clearFilters = () => {
    setStatusFilter(ALL);
    setSubjectFilter(ALL);
    setOwnerFilter(ALL);
    setUnreadOnly(false);
    setDateFrom('');
    setDateTo('');
    setSearchInput('');
    setQ('');
  };

  const open = (inquiry) =>
    navigate(`/admin/website-engagement/contact-inquiries/${inquiry.id}`);

  return (
    <WebsiteEngagementShell
      title="Contact Form Inquiries"
      description={
        'Messages sent through the contact form on the public website. Nobody here has a '
        + 'portal account — open one to read it in full, assign it, and reply by email.'
      }
    >
      <section
        aria-label="Contact inquiry summary"
        className="grid grid-cols-2 gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-4"
        data-testid="inquiry-summary"
      >
        <InquiryStatCard label="New" value={summary.new} icon={Inbox} tone="bg-[#D4AF37]/12 text-[#7A5A05]" accent />
        <InquiryStatCard label="Unread" value={summary.unread} icon={CircleDot} tone="bg-sky-50 text-sky-700" />
        <InquiryStatCard label="Replied" value={summary.replied} icon={MailCheck} tone="bg-emerald-50 text-emerald-700" />
        <InquiryStatCard label="Unassigned" value={summary.unassigned} icon={UserX} tone="bg-amber-50 text-amber-700" />
      </section>

      <TableFilterCard>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative max-w-xl flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#062B1A]/40" />
            <Input
              type="search"
              aria-label="Search contact inquiries"
              placeholder="Search name, email, subject, message, or reference..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className={cn('pl-9', adminInputClass)}
            />
          </div>
          <div className="text-xs text-[#062B1A]/60">{headerCount}</div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={statusFilter === ALL}
            onClick={() => setStatusFilter(ALL)}
            className={cn(filterPillClass, statusFilter === ALL && filterPillActiveClass)}
          >
            All
          </Button>
          {INQUIRY_STATUSES.map((status) => (
            <Button
              key={status}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={statusFilter === status}
              onClick={() => setStatusFilter(status)}
              className={cn(filterPillClass, statusFilter === status && filterPillActiveClass)}
            >
              {inquiryStatusMeta(status).label}
            </Button>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={unreadOnly}
            onClick={() => setUnreadOnly((current) => !current)}
            className={cn(filterPillClass, unreadOnly && filterPillActiveClass)}
          >
            Unread only
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={subjectFilter} onValueChange={setSubjectFilter}>
            <SelectTrigger aria-label="Subject filter" className={cn('h-8 w-full sm:w-[12rem]', adminInputClass)}>
              <SelectValue placeholder="Subject" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All subjects</SelectItem>
              {INQUIRY_SUBJECT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger aria-label="Owner filter" className={cn('h-8 w-full sm:w-[13rem]', adminInputClass)}>
              <SelectValue placeholder="Owner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Any owner</SelectItem>
              <SelectItem value={UNASSIGNED}>Nobody assigned</SelectItem>
              {owners.map((owner) => (
                <SelectItem key={owner.owner_user_id} value={owner.owner_user_id}>
                  {owner.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <label className="flex flex-1 items-center gap-1.5 text-xs text-[#062B1A]/60 sm:flex-none">
            From
            <Input
              type="date"
              aria-label="Received from"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className={cn('h-8 w-full sm:w-[10rem]', adminInputClass)}
            />
          </label>
          <label className="flex flex-1 items-center gap-1.5 text-xs text-[#062B1A]/60 sm:flex-none">
            To
            <Input
              type="date"
              aria-label="Received to"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className={cn('h-8 w-full sm:w-[10rem]', adminInputClass)}
            />
          </label>

          {hasActiveFilters && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 text-xs text-[#062B1A]/60 hover:text-[#062B1A]"
            >
              Clear filters
            </Button>
          )}
        </div>
      </TableFilterCard>

      {/* Nine columns and a 1,240px floor: readable at a desk, a sideways
          scroll on a phone. The same rows are rendered as cards below. */}
      <DesktopTableOnly>
      <Card className={tableCardClass}>
        <CardContent className="p-0">
          <div className="min-w-0 overflow-x-auto">
            <Table style={{ minWidth: TABLE_MIN_WIDTH }}>
              <TableHeader>
                <TableRow
                  className={cn('grid', tableHeaderRowClass)}
                  style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                >
                  {COLUMNS.map((column) => (
                    <TableHead
                      key={column.id}
                      className={cn(
                        tableHeadCellClass,
                        'flex items-center',
                        column.id === 'actions' && cn(STICKY_HEAD_RIGHT, 'justify-end'),
                      )}
                    >
                      {column.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                    <TableRow
                      key={`skeleton-${index}`}
                      className={cn('grid', tableRowClass)}
                      style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                      data-testid="inquiry-skeleton-row"
                    >
                      {COLUMNS.map((column) => (
                        <TableCell key={column.id} className={cn(tableCellClass, ROW_PADDING)}>
                          <div className="h-4 animate-pulse rounded bg-[#062B1A]/8" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableEmptyRow colSpan={COLUMNS.length}>
                    {hasActiveFilters
                      ? 'No inquiries match these filters.'
                      : 'No contact inquiries yet. Messages from the website contact form appear here.'}
                  </TableEmptyRow>
                ) : (
                  items.map((inquiry) => {
                    const meta = inquiryStatusMeta(inquiry.status);
                    return (
                      <TableRow
                        key={inquiry.id}
                        data-testid="inquiry-row"
                        className={cn('grid cursor-pointer', tableRowClass)}
                        style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                        onClick={() => open(inquiry)}
                      >
                        <TableCell className={cn(tableCellClass, ROW_PADDING, 'font-mono text-[11px] text-[#062B1A]/70')}>
                          <span className="flex items-center gap-1.5">
                            {inquiry.unread && (
                              <span
                                data-testid="unread-dot"
                                aria-label="Unread"
                                className="size-1.5 shrink-0 rounded-full bg-[#D4AF37]"
                              />
                            )}
                            <span className="truncate">{displayValue(inquiry.reference)}</span>
                          </span>
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING)}>
                          <span className={cn('truncate', inquiry.unread && 'font-semibold')}>
                            {displayValue(inquiry.full_name)}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING, 'truncate text-[#062B1A]/70')}>
                          {displayValue(inquiry.email)}
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING, 'truncate')}>
                          {inquiry.subject_label
                            || inquirySubjectLabel(inquiry.subject, inquiry.subject_original)}
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING, 'truncate text-[#062B1A]/60')}>
                          {displayValue(inquiry.message)}
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING, 'truncate text-[#062B1A]/70')}>
                          {inquiry.owner_name || 'Unassigned'}
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING)}>
                          <StatusPill tone={meta.pillTone}>{meta.label}</StatusPill>
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING, 'text-[#062B1A]/60')}>
                          {formatDate(inquiry.created_at)}
                        </TableCell>
                        <TableCell
                          className={cn(tableCellClass, ROW_PADDING, STICKY_RIGHT, 'flex justify-end')}
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={`Open inquiry ${inquiry.reference}`}
                            onClick={(event) => { event.stopPropagation(); open(inquiry); }}
                            className="h-7 px-2 text-[#062B1A]/70 hover:text-[#062B1A]"
                          >
                            <Eye className="size-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      </DesktopTableOnly>

      <MobileCardList data-testid="inquiry-card-list">
        {loading ? (
          Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <div
              key={`card-skeleton-${index}`}
              data-testid="inquiry-skeleton-card"
              className="h-24 animate-pulse rounded-xl border border-[rgba(6,43,26,0.08)] bg-white"
            />
          ))
        ) : items.length === 0 ? (
          <MobileCardNotice>
            {hasActiveFilters
              ? 'No inquiries match these filters.'
              : 'No contact inquiries yet. Messages from the website contact form appear here.'}
          </MobileCardNotice>
        ) : (
          items.map((inquiry) => {
            const meta = inquiryStatusMeta(inquiry.status);
            return (
              <MobileCard
                key={inquiry.id}
                data-testid="inquiry-card"
                onClick={() => open(inquiry)}
              >
                <MobileCardHeader
                  title={(
                    <span className="flex items-center gap-1.5">
                      {inquiry.unread && (
                        <span
                          aria-label="Unread"
                          className="size-1.5 shrink-0 rounded-full bg-[#D4AF37]"
                        />
                      )}
                      <span className={cn('truncate', inquiry.unread && 'font-bold')}>
                        {displayValue(inquiry.full_name)}
                      </span>
                    </span>
                  )}
                  meta={displayValue(inquiry.email)}
                  badge={<StatusPill tone={meta.pillTone}>{meta.label}</StatusPill>}
                />
                {/* The message is why somebody opens this row, so it gets
                    two lines of its own rather than a truncated cell. */}
                <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-[#062B1A]/70">
                  {displayValue(inquiry.message)}
                </p>
                <MobileCardFields>
                  <MobileCardField label="Subject">
                    {inquiry.subject_label
                      || inquirySubjectLabel(inquiry.subject, inquiry.subject_original)}
                  </MobileCardField>
                  <MobileCardField label="Owner">
                    {inquiry.owner_name || 'Unassigned'}
                  </MobileCardField>
                  <MobileCardField label="Received">
                    {formatDate(inquiry.created_at)}
                  </MobileCardField>
                  <MobileCardField label="Reference">
                    <span className="font-mono">{displayValue(inquiry.reference)}</span>
                  </MobileCardField>
                </MobileCardFields>
              </MobileCard>
            );
          })
        )}
      </MobileCardList>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-[#062B1A]/55">Page {page + 1} of {pageCount}</p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page === 0 || loading}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
            >
              <ChevronLeft className="mr-1 size-4" /> Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page + 1 >= pageCount || loading}
              onClick={() => setPage((current) => current + 1)}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
            >
              Next <ChevronRight className="ml-1 size-4" />
            </Button>
          </div>
        </div>
      )}
    </WebsiteEngagementShell>
  );
}
